import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiDownload,
  FiUser,
  FiUsers,
  FiUserCheck,
  FiMoreHorizontal,
  FiEye,
  FiCheckSquare,
  FiTrash2,
  FiPauseCircle,
  FiChevronLeft,
  FiChevronRight,
  FiUserPlus,
  FiX,
} from "react-icons/fi";

const ViewAdmins = () => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 50,
    current_page: 1,
    last_page: 1,
    from: null,
    to: null
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignRoleData, setAssignRoleData] = useState({
    role: "",
    state_code: "",
    sd_code: "",
    fc_code: "",
    lcda_code: "",
    lg_code: "",
    ward_code: "",
    pu_code: "",
    action: "assign"
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [geographicData, setGeographicData] = useState({
    states: [],
    senatorialDistricts: [],
    federalConstituencies: [],
    lcdas: [],
    lgs: [],
    wards: [],
    pollingUnits: []
  });
  const [isMobile, setIsMobile] = useState(false);

  // API Base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.tinubu-ambode.org";

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get auth token with better error handling
  const getAuthToken = () => {
    try {
      const token = localStorage.getItem("authToken") || 
                   localStorage.getItem("token") ||
                   sessionStorage.getItem("authToken") ||
                   sessionStorage.getItem("token");
      
      if (!token) {
        console.warn("No authentication token found");
        return null;
      }
      
      return token;
    } catch (error) {
      console.error("Error retrieving auth token:", error);
      return null;
    }
  };

  // Fetch admins data - SHOW ALL ADMINS INCLUDING SUPER ADMINS
  const fetchData = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }

      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...(search && { search: search })
      });

      const url = `${API_BASE_URL}/api/v1/admins?${queryParams}`;

      console.log("🌐 Fetching admins from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      console.log("📡 API Response status:", response.status);

      if (response.status === 401) {
        // Clear invalid token
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("token");
        throw new Error("Authentication failed. Please log in again.");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ API Response data:", result);
      
      if (result.success || result.status === "success") {
        // SHOW ALL ADMINS - NO FILTERING
        const adminsData = result.data || result.admins || [];
        setAdmins(adminsData);
        setPagination({
          total: result.total || result.pagination?.total || adminsData.length,
          per_page: result.per_page || result.pagination?.per_page || 50,
          current_page: result.current_page || result.pagination?.current_page || 1,
          last_page: result.last_page || result.pagination?.last_page || 1,
          from: result.from || result.pagination?.from || 1,
          to: result.to || result.pagination?.to || adminsData.length
        });
      } else {
        throw new Error(result.message || "API returned unsuccessful response");
      }
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load geographic data for admin assignment
  const fetchGeographicData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      // Fetch states
      const statesResponse = await fetch(`${API_BASE_URL}/api/v1/geographical/states`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (statesResponse.ok) {
        const statesData = await statesResponse.json();
        if (statesData.success || statesData.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            states: statesData.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching geographic data:", err);
    }
  };

  // Fetch senatorial districts based on state
  const fetchSenatorialDistricts = async (stateCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/senatorial-districts?state_code=${stateCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            senatorialDistricts: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching senatorial districts:", err);
    }
  };

  // Fetch federal constituencies based on state
  const fetchFederalConstituencies = async (stateCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/federal-constituencies?state_code=${stateCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            federalConstituencies: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching federal constituencies:", err);
    }
  };

  // Fetch LCDAs based on state
  const fetchLCDAs = async (stateCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/lcdas?state_code=${stateCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            lcdas: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching LCDAs:", err);
    }
  };

  // Fetch LGs based on state
  const fetchLGs = async (stateCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/lgs?state_code=${stateCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            lgs: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching LGs:", err);
    }
  };

  // Fetch wards based on LG
  const fetchWards = async (lgCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/wards?lg_code=${lgCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            wards: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching wards:", err);
    }
  };

  // Fetch polling units based on ward
  const fetchPollingUnits = async (wardCode) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/geographical/polling-units?ward_code=${wardCode}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.status === "success") {
          setGeographicData(prev => ({
            ...prev,
            pollingUnits: data.data || []
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching polling units:", err);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
    fetchGeographicData();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Debounce search - wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      fetchData(1, value);
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      fetchData(newPage, searchTerm);
    }
  };

  // Handle user click - navigate to user details
  const handleUserClick = (userId) => {
    navigate(`/admin/user-details/${userId}`);
  };

  // Handle view details from dropdown
  const handleViewDetails = (userId) => {
    navigate(`/admin/user-details/${userId}`);
    setActiveDropdown(null);
  };

  // Handle assign role
  const handleAssignRole = (user) => {
    setSelectedUser(user);
    setAssignRoleData({
      role: "",
      state_code: "NG-LA", // Default to Lagos
      sd_code: "",
      fc_code: "",
      lcda_code: "",
      lg_code: "",
      ward_code: "",
      pu_code: "",
      action: "assign"
    });
    setShowAssignRoleModal(true);
    setActiveDropdown(null);
    
    // Pre-fetch data for default state
    fetchSenatorialDistricts("NG-LA");
    fetchFederalConstituencies("NG-LA");
    fetchLCDAs("NG-LA");
    fetchLGs("NG-LA");
  };

  // Handle remove admin - DELETE method for admin removal
  const handleRemoveAdmin = async (user) => {
    if (!window.confirm(`Are you sure you want to remove admin privileges from ${user.full_name}? This will revoke their admin access but keep their account as a regular member.`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Use DELETE method for admin removal
      const response = await fetch(`${API_BASE_URL}/api/v1/admins/${user.id}/revoke`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      const result = await response.json();

      if (result.success || result.status === "success") {
        alert("Admin privileges removed successfully!");
        fetchData(); // Refresh data
      } else {
        throw new Error(result.message || "Failed to remove admin privileges");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle delete user - DELETE method for permanent deletion
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${user.full_name}? This action cannot be undone!`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Use DELETE method for permanent deletion
      const response = await fetch(`${API_BASE_URL}/api/v1/admins/${user.id}/revoke`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      const result = await response.json();

      if (result.success || result.status === "success") {
        alert("Admin deleted successfully!");
        fetchData(); // Refresh data
      } else {
        throw new Error(result.message || "Failed to delete admin");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle suspend user
  const handleSuspendUser = async (user) => {
    if (!window.confirm(`Are you sure you want to suspend ${user.full_name}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      alert("Suspend functionality will be implemented when the suspend endpoint is available");
      
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle approve admin
  const handleApproveAdmin = async (user) => {
    if (!window.confirm(`Are you sure you want to approve ${user.full_name}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      alert("Approve admin functionality will be implemented when the approve endpoint is available");
      
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle dropdown click
  const handleDropdownClick = (e, index) => {
    e.stopPropagation();
    
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = isMobile ? 160 : 180;
    const dropdownHeight = 200;
    
    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;
    
    // Check if there's enough space below, if not open above
    if (top + dropdownHeight > window.innerHeight) {
      top = buttonRect.top - dropdownHeight - 5;
    }
    
    // Check if there's enough space on the right, if not adjust left
    if (left + dropdownWidth > window.innerWidth) {
      left = window.innerWidth - dropdownWidth - 10;
    }
    
    setDropdownPosition({ top, left });
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Handle state code change
  const handleStateCodeChange = (stateCode) => {
    setAssignRoleData(prev => ({
      ...prev,
      state_code: stateCode,
      sd_code: "",
      fc_code: "",
      lcda_code: "",
      lg_code: "",
      ward_code: "",
      pu_code: ""
    }));

    // Fetch geographic data based on selected state
    if (stateCode) {
      fetchSenatorialDistricts(stateCode);
      fetchFederalConstituencies(stateCode);
      fetchLCDAs(stateCode);
      fetchLGs(stateCode);
    }
  };

  // Handle LG code change
  const handleLGCodeChange = (lgCode) => {
    setAssignRoleData(prev => ({
      ...prev,
      lg_code: lgCode,
      ward_code: "",
      pu_code: ""
    }));

    // Fetch wards based on selected LG
    if (lgCode) {
      fetchWards(lgCode);
    }
  };

  // Handle ward code change
  const handleWardCodeChange = (wardCode) => {
    setAssignRoleData(prev => ({
      ...prev,
      ward_code: wardCode,
      pu_code: ""
    }));

    // Fetch polling units based on selected ward
    if (wardCode) {
      fetchPollingUnits(wardCode);
    }
  };

  // Check if required fields are filled based on role
  const isFormValid = () => {
    const { role, state_code, sd_code, fc_code, lcda_code, lg_code, ward_code, pu_code } = assignRoleData;

    if (!role || !state_code) return false;

    switch (role) {
      case "super-admin":
        return true; // No geographic restrictions
      case "state-admin":
        return true; // Only state_code required
      case "sd-admin":
        return !!sd_code; // Requires senatorial district
      case "fc-admin":
        return !!fc_code; // Requires federal constituency
      case "lcda-admin":
        return !!lcda_code; // Requires LCDA
      case "lga-admin":
        return !!lg_code; // Requires LG
      case "ward-admin":
        return !!lg_code && !!ward_code; // Requires LG and ward
      case "pu-admin":
        return !!lg_code && !!ward_code && !!pu_code; // Requires LG, ward and polling unit
      default:
        return false;
    }
  };

  // Get required geographic fields based on role
  const getRequiredFields = (role) => {
    switch (role) {
      case "super-admin":
        return [];
      case "state-admin":
        return ["state"];
      case "sd-admin":
        return ["state", "senatorial_district"];
      case "fc-admin":
        return ["state", "federal_constituency"];
      case "lcda-admin":
        return ["state", "lcda"];
      case "lga-admin":
        return ["state", "lg"];
      case "ward-admin":
        return ["state", "lg", "ward"];
      case "pu-admin":
        return ["state", "lg", "ward", "polling_unit"];
      default:
        return [];
    }
  };

  // Submit assign role
  const handleAssignRoleSubmit = async () => {
    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Prepare the request data based on role type
      const requestData = {
        user_id: selectedUser.id,
        role: assignRoleData.role,
        action: assignRoleData.action,
        state_code: assignRoleData.state_code
      };

      // Add specific geographic codes based on role
      switch (assignRoleData.role) {
        case "sd-admin":
          requestData.sd_code = assignRoleData.sd_code;
          break;
        case "fc-admin":
          requestData.fc_code = assignRoleData.fc_code;
          break;
        case "lcda-admin":
          requestData.lcda_code = assignRoleData.lcda_code;
          break;
        case "lga-admin":
          requestData.lg_code = assignRoleData.lg_code;
          break;
        case "ward-admin":
          requestData.lg_code = assignRoleData.lg_code;
          requestData.ward_code = assignRoleData.ward_code;
          break;
        case "pu-admin":
          requestData.lg_code = assignRoleData.lg_code;
          requestData.ward_code = assignRoleData.ward_code;
          requestData.pu_code = assignRoleData.pu_code;
          break;
        // super-admin and state-admin don't need additional codes
      }

      console.log("🚀 Submitting role assignment:", requestData);

      const response = await fetch(`${API_BASE_URL}/api/v1/admins/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        },
        body: JSON.stringify(requestData)
      });

      const responseText = await response.text();
      console.log("📡 Raw response:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        throw new Error("Server returned invalid JSON response");
      }

      if (!response.ok) {
        if (response.status === 422) {
          // Handle validation errors
          const errorDetails = result.errors ? Object.values(result.errors).flat().join(', ') : result.message;
          throw new Error(`Validation failed: ${errorDetails}`);
        }
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success || result.status === "success") {
        alert("Role assigned successfully!");
        setShowAssignRoleModal(false);
        fetchData(); // Refresh data
      } else {
        throw new Error(result.message || "Failed to assign role");
      }
    } catch (err) {
      console.error("❌ Error assigning role:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const statusBadge = (status) => {
    let bg = "#f3f4f6", color = "#6b7280";
    
    switch (status?.toLowerCase()) {
      case "approved":
      case "active":
        bg = "#dcfce7";
        color = "#16a34a";
        break;
      case "pending":
        bg = "#fef9c3";
        color = "#ca8a04";
        break;
      case "suspended":
      case "revoked":
        bg = "#fee2e2";
        color = "#dc2626";
        break;
      default:
        bg = "#f3f4f6";
        color = "#6b7280";
    }
    
    return {
      backgroundColor: bg,
      color: color,
      borderRadius: "10px",
      padding: "2px 6px",
      fontSize: isMobile ? "9px" : "10px",
      fontWeight: "600",
      textTransform: "capitalize"
    };
  };

  const roleBadge = (roles) => {
    let bg = "#dbeafe", color = "#2563eb";
    
    const role = roles?.[0]?.toLowerCase();
    switch (role) {
      case "super-admin":
        bg = "#fef3c7";
        color = "#d97706";
        break;
      case "state-admin":
        bg = "#bbf7d0";
        color = "#166534";
        break;
      case "sd-admin":
        bg = "#e0e7ff";
        color = "#1e3a8a";
        break;
      case "fc-admin":
        bg = "#fce7f3";
        color = "#db2777";
        break;
      case "lcda-admin":
        bg = "#d1fae5";
        color = "#065f46";
        break;
      case "lga-admin":
        bg = "#f3e8ff";
        color = "#7c3aed";
        break;
      case "ward-admin":
        bg = "#fce7f3";
        color = "#db2777";
        break;
      case "pu-admin":
        bg = "#e0e7ff";
        color = "#1e3a8a";
        break;
      default:
        bg = "#dbeafe";
        color = "#2563eb";
    }
    
    return {
      backgroundColor: bg,
      color: color,
      borderRadius: "10px",
      padding: "2px 6px",
      fontSize: isMobile ? "9px" : "10px",
      fontWeight: "600",
      textTransform: "capitalize"
    };
  };

  // Calculate display range for admin count
  const getDisplayRange = () => {
    const { from, to, total } = pagination;
    if (total === 0) return "0 - 0 of 0";
    return `${from || 1} - ${to || admins.length} of ${total}`;
  };

  // Count admins by status for stats
  const approvedAdmins = admins.filter(m => m.status === "approved").length;
  const superAdmins = admins.filter(m => m.roles?.includes("super-admin")).length;

  if (loading && admins.length === 0) {
    return (
      <div style={{ 
        padding: "20px", 
        fontFamily: "'Inter', sans-serif", 
        background: "#fff", 
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "14px"
      }}>
        <div>Loading admins...</div>
      </div>
    );
  }

  const requiredFields = getRequiredFields(assignRoleData.role);

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "20px",
        fontFamily: "'Inter', sans-serif",
        background: "#fff",
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: "16px",
          flexDirection: isMobile ? "column" : "row",
          gap: "12px"
        }}
      >
        <div style={{ 
          width: isMobile ? "100%" : "auto",
          marginBottom: isMobile ? "8px" : "0"
        }}>
          <h2 style={{ 
            fontSize: isMobile ? "16px" : "18px", 
            fontWeight: "700", 
            color: "#111827",
            marginBottom: "2px"
          }}>
            Admin Management
          </h2>
          <p style={{ 
            fontSize: isMobile ? "11px" : "12px", 
            color: "#6b7280",
            margin: 0
          }}>
            Manage system administrators and their permissions
          </p>
        </div>

        {/* RIGHT SIDE BUTTONS */}
        <div style={{ 
          display: "flex", 
          gap: "6px", 
          alignItems: isMobile ? "stretch" : "flex-end",
          width: isMobile ? "100%" : "auto"
        }}>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              padding: isMobile ? "8px 12px" : "6px 12px",
              fontSize: isMobile ? "12px" : "11px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flex: isMobile ? 1 : "none",
            }}
          >
            <FiDownload size={isMobile ? 14 : 12} /> 
            {isMobile ? "Export" : "Export Admins"}
          </button>
        </div>
      </div>

      {/* AUTH ERROR MESSAGE */}
      {error && (
        <div style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "5px",
          padding: "10px 12px",
          marginBottom: "16px",
          color: "#dc2626",
          fontSize: isMobile ? "12px" : "11px"
        }}>
          {error}
          <button 
            onClick={() => fetchData()}
            style={{ 
              marginLeft: "8px", 
              padding: "4px 8px", 
              background: "#dc2626", 
              color: "white", 
              border: "none", 
              borderRadius: "3px",
              cursor: "pointer",
              fontSize: isMobile ? "11px" : "10px"
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* SEARCH */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "16px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "4px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "300px",
        }}
      >
        <FiSearch color="#3b82f6" size={isMobile ? 14 : 12} />
        <input
          type="text"
          placeholder="Search by name, email..."
          value={searchTerm}
          onChange={handleSearch}
          style={{
            flex: "1",
            fontSize: isMobile ? "14px" : "12px",
            border: "none",
            outline: "none",
            background: "transparent",
            color: "#374151",
            padding: isMobile ? "6px 0" : "4px 0",
          }}
        />
      </div>

      {/* STATS CARDS */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(180px, 1fr))", 
        gap: "12px",
        marginBottom: "20px"
      }}>
        <StatCard 
          icon={<FiUser size={isMobile ? 18 : 16} />} 
          title="Total Administrators" 
          value={pagination.total.toLocaleString()} 
          color="#2563eb" 
          isMobile={isMobile}
        />
        <StatCard 
          icon={<FiUsers size={isMobile ? 18 : 16} />} 
          title="Active Admins" 
          value={approvedAdmins.toString()} 
          color="#22c55e" 
          isMobile={isMobile}
        />
        <StatCard 
          icon={<FiUserCheck size={isMobile ? 18 : 16} />} 
          title="Super Admins" 
          value={superAdmins.toString()} 
          color="#2563eb" 
          isMobile={isMobile}
        />
      </div>

      {/* DATA COUNT */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "12px",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "8px" : "0"
      }}>
        <p style={{ 
          color: "#6b7280", 
          fontSize: isMobile ? "12px" : "11px",
          fontWeight: isMobile ? "500" : "normal"
        }}>
          Administrators List
        </p>
        <p style={{ 
          color: "#6b7280", 
          fontSize: isMobile ? "11px" : "10px" 
        }}>
          {getDisplayRange()}
        </p>
      </div>

      {/* TABLE CONTAINER - Mobile Cards View */}
      {isMobile ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "16px"
        }}>
          {admins.length === 0 ? (
            <div style={{ 
              textAlign: "center", 
              padding: "40px 20px", 
              color: "#6b7280",
              fontSize: "14px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px"
            }}>
              {error ? `Error: ${error}` : "No administrators found"}
            </div>
          ) : (
            admins.map((item, i) => (
              <div 
                key={item.id} 
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px",
                  background: "#fff",
                  position: "relative"
                }}
              >
                {/* Header Row */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "8px"
                }}>
                  <div style={{ flex: 1 }}>
                    <div 
                      style={{ 
                        fontWeight: "600", 
                        color: "#111827", 
                        fontSize: "14px",
                        cursor: "pointer",
                        marginBottom: "2px"
                      }}
                      onClick={() => handleUserClick(item.id)}
                    >
                      {item.full_name}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "4px"
                    }}>
                      {item.email}
                    </div>
                  </div>
                  <span
                    onClick={(e) => handleDropdownClick(e, i)}
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      color: "#6b7280",
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px",
                      borderRadius: "4px",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <FiMoreHorizontal />
                  </span>
                </div>

                {/* Details Row */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>ID:</span>
                    <div style={{ 
                      background: "#f3f4f6", 
                      padding: "2px 6px", 
                      borderRadius: "3px", 
                      fontSize: "10px",
                      color: "#374151",
                      fontFamily: "monospace"
                    }}>
                      {item.membership_id}
                    </div>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Status:</span>
                    <span style={statusBadge(item.status)}>{item.status}</span>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Role:</span>
                    <span style={roleBadge(item.roles)}>
                      {item.roles?.[0] === "super-admin" ? "Super" : 
                       item.roles?.[0] === "state-admin" ? "State" :
                       item.roles?.[0] === "sd-admin" ? "Senatorial" :
                       item.roles?.[0] === "fc-admin" ? "Federal" :
                       item.roles?.[0] === "lcda-admin" ? "LCDA" :
                       item.roles?.[0] === "lga-admin" ? "LGA" :
                       item.roles?.[0] === "ward-admin" ? "Ward" :
                       item.roles?.[0] === "pu-admin" ? "Polling Unit" : "Admin"}
                    </span>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Join Date:</span>
                    <span style={{ color: "#2563eb", fontWeight: "500", fontSize: "11px" }}>
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* DESKTOP TABLE VIEW */
        <div style={{
          width: "100%",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          overflow: "hidden",
          position: "relative",
        }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            position: "relative",
          }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={tableHeaderStyle(30)}></th>
                <th style={tableHeaderStyle(120)}>Name</th>
                <th style={tableHeaderStyle(150)}>Email</th>
                <th style={tableHeaderStyle(120)}>ID</th>
                <th style={tableHeaderStyle(80)}>Status</th>
                <th style={tableHeaderStyle(100)}>Role</th>
                <th style={tableHeaderStyle(90)}>Join Date</th>
                <th style={tableHeaderStyle(60)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ 
                    textAlign: "center", 
                    padding: "30px", 
                    color: "#6b7280",
                    fontSize: "12px"
                  }}>
                    {error ? `Error: ${error}` : "No administrators found"}
                  </td>
                </tr>
              ) : (
                admins.map((item, i) => (
                  <tr 
                    key={item.id} 
                    style={{ 
                      borderBottom: "1px solid #f3f4f6",
                      position: "relative",
                    }}
                  >
                    <td style={{ ...tableCellStyle, width: "30px", textAlign: "center" }}>
                      <input 
                        type="checkbox" 
                        style={{
                          width: "14px",
                          height: "14px",
                        }}
                      />
                    </td>
                    <td style={{ ...tableCellStyle, width: "120px" }}>
                      <div 
                        style={{ 
                          fontWeight: "600", 
                          color: "#111827", 
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                        onClick={() => handleUserClick(item.id)}
                      >
                        {item.full_name}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "150px", color: "#6b7280" }}>
                      <div style={{
                        fontSize: "11px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {item.email}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "120px" }}>
                      <div style={{ 
                        background: "#f3f4f6", 
                        padding: "2px 6px", 
                        borderRadius: "3px", 
                        fontSize: "10px",
                        color: "#374151",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {item.membership_id}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "80px" }}>
                      <span style={statusBadge(item.status)}>{item.status}</span>
                    </td>
                    <td style={{ ...tableCellStyle, width: "100px" }}>
                      <span style={roleBadge(item.roles)}>
                        {item.roles?.[0] === "super-admin" ? "Super" : 
                         item.roles?.[0] === "state-admin" ? "State" :
                         item.roles?.[0] === "sd-admin" ? "Senatorial" :
                         item.roles?.[0] === "fc-admin" ? "Federal" :
                         item.roles?.[0] === "lcda-admin" ? "LCDA" :
                         item.roles?.[0] === "lga-admin" ? "LGA" :
                         item.roles?.[0] === "ward-admin" ? "Ward" :
                         item.roles?.[0] === "pu-admin" ? "Polling Unit" : "Admin"}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, width: "90px", color: "#2563eb", fontWeight: "500", fontSize: "11px" }}>
                      {formatDate(item.created_at)}
                    </td>
                    <td style={{ ...tableCellStyle, width: "60px", position: "relative" }}>
                      <span
                        onClick={(e) => handleDropdownClick(e, i)}
                        style={{
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#6b7280",
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px",
                          borderRadius: "4px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#f3f4f6"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                      >
                        <FiMoreHorizontal />
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* FLOATING DROPDOWN */}
      {activeDropdown !== null && (
        <>
          <div
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              background: "#fff",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
              borderRadius: "8px",
              zIndex: 9999,
              padding: "8px 0",
              width: isMobile ? "160px" : "180px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: isMobile ? "10px 12px" : "8px 12px",
                fontSize: isMobile ? "13px" : "12px",
                cursor: "pointer",
                color: "#111827",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleViewDetails(admins[activeDropdown].id)}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiEye size={isMobile ? 14 : 12} /> View Details
            </div>
            
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: isMobile ? "10px 12px" : "8px 12px",
                fontSize: isMobile ? "13px" : "12px",
                cursor: "pointer",
                color: "#7c3aed",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleAssignRole(admins[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiUserPlus size={isMobile ? 14 : 12} /> Assign Admin Role
            </div>

            <div style={{ height: "1px", background: "#f3f4f6", margin: "4px 0" }}></div>
            
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#16a34a",
              transition: "background-color 0.2s",
            }}
              onClick={() => handleApproveAdmin(admins[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiCheckSquare size={isMobile ? 14 : 12} /> Approve
            </div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#ea580c",
              transition: "background-color 0.2s",
            }}
              onClick={() => handleSuspendUser(admins[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiPauseCircle size={isMobile ? 14 : 12} /> Suspend
            </div>
            <div style={{ height: "1px", background: "#f3f4f6", margin: "4px 0" }}></div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#dc2626",
              transition: "background-color 0.2s",
            }}
              onClick={() => handleRemoveAdmin(admins[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiTrash2 size={isMobile ? 14 : 12} /> Remove Admin
            </div>
          </div>
          
          {/* Click outside overlay */}
          <div 
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              background: "transparent",
            }}
            onClick={() => setActiveDropdown(null)}
          />
        </>
      )}

      {/* PAGINATION */}
      {pagination.last_page > 1 && (
        <div style={{ 
          display: "flex", 
          justifyContent: isMobile ? "space-between" : "flex-end", 
          alignItems: "center", 
          marginTop: "16px",
          gap: "6px" 
        }}>
          <button 
            style={paginationBtn(pagination.current_page === 1, isMobile)} 
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
          >
            <FiChevronLeft size={isMobile ? 14 : 12} />
            {isMobile && "Previous"}
          </button>
          
          <span style={{ 
            fontSize: isMobile ? "13px" : "12px", 
            color: "#374151",
            textAlign: "center",
            flex: isMobile ? 1 : "none"
          }}>
            Page {pagination.current_page} of {pagination.last_page}
          </span>
          
          <button 
            style={paginationBtn(pagination.current_page === pagination.last_page, isMobile)} 
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
          >
            {isMobile && "Next"}
            <FiChevronRight size={isMobile ? 14 : 12} />
          </button>
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignRoleModal && (
        <div style={modalOverlayStyle}>
          <div style={{
            ...modalContentStyle,
            width: isMobile ? "95%" : "480px",
            maxHeight: isMobile ? "90vh" : "auto",
            overflowY: "auto"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              <h3 style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                color: "#111827",
                margin: 0,
                display: "flex",
                alignItems: "center"
              }}>
                <FiUserPlus style={{ marginRight: "8px" }} />
                Assign Admin Role
              </h3>
              <button
                onClick={() => setShowAssignRoleModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "4px"
                }}
              >
                <FiX />
              </button>
            </div>
            
            <div style={modalBodyStyle}>
              <p style={{ 
                fontSize: isMobile ? "13px" : "14px", 
                color: "#6b7280", 
                marginBottom: "16px" 
              }}>
                Assign admin role to <strong>{selectedUser?.full_name}</strong>
              </p>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Admin Role *</label>
                <select
                  value={assignRoleData.role}
                  onChange={(e) => setAssignRoleData({...assignRoleData, role: e.target.value})}
                  style={selectStyle}
                >
                  <option value="">Select Role</option>
                  <option value="super-admin">Super Admin</option>
                  <option value="state-admin">State Admin</option>
                  <option value="sd-admin">Senatorial District Admin</option>
                  <option value="fc-admin">Federal Constituency Admin</option>
                  <option value="lcda-admin">LCDA Admin</option>
                  <option value="lga-admin">Local Government Admin</option>
                  <option value="ward-admin">Ward Admin</option>
                  <option value="pu-admin">Polling Unit Admin</option>
                </select>
              </div>

              {/* Geographic Selection Based on Role */}
              {assignRoleData.role && assignRoleData.role !== "super-admin" && (
                <>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>State *</label>
                    <select
                      value={assignRoleData.state_code}
                      onChange={(e) => handleStateCodeChange(e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Select State</option>
                      {geographicData.states.map(state => (
                        <option key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Senatorial District (for sd-admin) */}
                  {requiredFields.includes("senatorial_district") && assignRoleData.state_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Senatorial District *</label>
                      <select
                        value={assignRoleData.sd_code}
                        onChange={(e) => setAssignRoleData({...assignRoleData, sd_code: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="">Select Senatorial District</option>
                        {geographicData.senatorialDistricts.map(sd => (
                          <option key={sd.code} value={sd.code}>
                            {sd.name} ({sd.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Federal Constituency (for fc-admin) */}
                  {requiredFields.includes("federal_constituency") && assignRoleData.state_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Federal Constituency *</label>
                      <select
                        value={assignRoleData.fc_code}
                        onChange={(e) => setAssignRoleData({...assignRoleData, fc_code: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="">Select Federal Constituency</option>
                        {geographicData.federalConstituencies.map(fc => (
                          <option key={fc.code} value={fc.code}>
                            {fc.name} ({fc.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* LCDA (for lcda-admin) */}
                  {requiredFields.includes("lcda") && assignRoleData.state_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>LCDA *</label>
                      <select
                        value={assignRoleData.lcda_code}
                        onChange={(e) => setAssignRoleData({...assignRoleData, lcda_code: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="">Select LCDA</option>
                        {geographicData.lcdas.map(lcda => (
                          <option key={lcda.code} value={lcda.code}>
                            {lcda.name} ({lcda.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* LG (for lga-admin, ward-admin, pu-admin) */}
                  {requiredFields.includes("lg") && assignRoleData.state_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Local Government *</label>
                      <select
                        value={assignRoleData.lg_code}
                        onChange={(e) => handleLGCodeChange(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="">Select Local Government</option>
                        {geographicData.lgs.map(lg => (
                          <option key={lg.code} value={lg.code}>
                            {lg.name} ({lg.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Ward (for ward-admin, pu-admin) */}
                  {requiredFields.includes("ward") && assignRoleData.lg_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Ward *</label>
                      <select
                        value={assignRoleData.ward_code}
                        onChange={(e) => handleWardCodeChange(e.target.value)}
                        style={selectStyle}
                      >
                        <option value="">Select Ward</option>
                        {geographicData.wards.map(ward => (
                          <option key={ward.code} value={ward.code}>
                            {ward.name} ({ward.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Polling Unit (for pu-admin) */}
                  {requiredFields.includes("polling_unit") && assignRoleData.ward_code && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>Polling Unit *</label>
                      <select
                        value={assignRoleData.pu_code}
                        onChange={(e) => setAssignRoleData({...assignRoleData, pu_code: e.target.value})}
                        style={selectStyle}
                      >
                        <option value="">Select Polling Unit</option>
                        {geographicData.pollingUnits.map(pu => (
                          <option key={pu.code} value={pu.code}>
                            {pu.name} ({pu.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div style={formGroupStyle}>
                <label style={labelStyle}>Action</label>
                <select
                  value={assignRoleData.action}
                  onChange={(e) => setAssignRoleData({...assignRoleData, action: e.target.value})}
                  style={selectStyle}
                >
                  <option value="assign">Assign Role</option>
                  <option value="remove">Remove Role</option>
                </select>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <button
                onClick={() => setShowAssignRoleModal(false)}
                style={cancelButtonStyle}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRoleSubmit}
                disabled={!isFormValid() || actionLoading}
                style={{
                  ...submitButtonStyle,
                  opacity: (!isFormValid() || actionLoading) ? 0.6 : 1,
                  cursor: (!isFormValid() || actionLoading) ? "not-allowed" : "pointer"
                }}
              >
                {actionLoading ? "Assigning..." : "Assign Role"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- COMPONENTS ---
const StatCard = ({ icon, title, value, color, isMobile }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: "6px",
      padding: isMobile ? "16px" : "12px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div>
      <p style={{ 
        fontSize: isMobile ? "12px" : "11px", 
        color: "#6b7280", 
        margin: "0 0 4px 0" 
      }}>
        {title}
      </p>
      <h3 style={{ 
        fontSize: isMobile ? "18px" : "16px", 
        fontWeight: "700", 
        color: "#111827", 
        margin: 0 
      }}>
        {value}
      </h3>
    </div>
    <div style={{ color: color, fontSize: isMobile ? "18px" : "16px" }}>{icon}</div>
  </div>
);

const paginationBtn = (disabled, isMobile) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  background: "#fff",
  border: `1px solid ${disabled ? "#e5e7eb" : "#d1d5db"}`,
  borderRadius: "5px",
  padding: isMobile ? "8px 12px" : "6px 10px",
  fontSize: isMobile ? "13px" : "11px",
  color: disabled ? "#9ca3af" : "#374151",
  cursor: disabled ? "not-allowed" : "pointer",
  minWidth: isMobile ? "auto" : "60px",
});

// Compact table styles
const tableHeaderStyle = (width) => ({
  textAlign: "left",
  padding: "8px 10px",
  fontSize: "11px",
  color: "#374151",
  fontWeight: "600",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  width: `${width}px`
});

const tableCellStyle = {
  padding: "8px 10px",
  fontSize: "11px",
  fontWeight: "500",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

// Modal Styles
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 2000,
  padding: "20px"
};

const modalContentStyle = {
  background: "#fff",
  borderRadius: "12px",
  padding: "24px",
  width: "100%",
  maxWidth: "480px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

const modalBodyStyle = {
  marginBottom: "24px"
};

const modalFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px"
};

const formGroupStyle = {
  marginBottom: "16px"
};

const labelStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "500",
  color: "#374151",
  marginBottom: "6px"
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box"
};

const selectStyle = {
  ...inputStyle,
  background: "white"
};

const cancelButtonStyle = {
  padding: "10px 20px",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  color: "#374151",
  cursor: "pointer",
  fontWeight: "500"
};

const submitButtonStyle = {
  padding: "10px 20px",
  background: "#2563eb",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  color: "white",
  cursor: "pointer",
  fontWeight: "500"
};

export default ViewAdmins;